<template>
    <header-banner title="Retirement choices"></header-banner>

    <div class="col-md-9 mx-auto">
        <div class="row">
            <div class="col-md-6">
                <iframe src="https://www.youtube.com/embed/d0lyODJaEiU?si=JlR7yTOZ7yAA4U-u" class="w-100 h-100" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
            </div>
            <div class="col-md-6">
                <h4>What are my retirement options?</h4>
                <p>The earliest you can access your pension is usually 55 (rising to 57 from 2028).</p>
                <p>It's up to you when you take it. And if you don't need the money yet, you can leave it for future. Below we compare the main retirement options for taking money for your pension.</p>
                <p>The government's free and impartial <a href="https://www.moneyhelper.org.uk/en/pensions-and-retirement/pension-wise" target="_blank">Pension Wise</a> service can help those aged 50 or over understand what type of pension they have, how to access their pension savings and the potential tax implications of each option.</p>
                <p>Tax rules can change, and the benefits will depend on your own circumstances.</p>
            </div>
        </div>
        <div class="row mt-4 mb-5">
            <div class="col">
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <h5 class="mb-0">Read the video transcript</h5>
                        <button class="btn btn-sm btn-primary" type="button" data-bs-toggle="collapse" data-bs-target="#videoTranscript" aria-expanded="false" aria-controls="videoTranscript">
                            Read Transcript
                        </button>
                    </div>
                    <div class="card-body p-4 collapse" id="videoTranscript">
                        <div class="collapse" id="videoTranscript">
                            <p class="card-text">
                                What are my retirement options? Three ways of taking income from your pension explained. Animation and illustrations play on screen that highlights the phrases used in the voiceover.
                            </p>
                            <p class="card-text">
                                "You've been building up your retirement savings, and now might be the time when you're thinking of taking some money out. You'll probably want an income and you might want to take an initial lump sum. For example for some home improvements or to pay off some debt. But there are a number of ways you can do this, so it's worth taking the time to consider your options and see which ways will work best for you.
                            </p>
                            <p class="card-text">
                                The earliest you can take your pension is age 55, rising to 57 from 2028. Usually you can take up to a quarter of it tax-free, with the rest taxed as income when you take it out. Here are three different ways you can take the money out of your pension."
                            </p>
                            <p class="card-text">
                                <strong>Annuities:</strong>
                            </p>
                            <p class="card-text">
                                "Some bills never stop so for peace of mind you could make sure that your essential expenditure is covered with a secure income. You always have the option to convert some or all of your pension savings to an annuity, which will pay you a guaranteed income for life. At the start you can choose different options, like inflation protection or a continuing income for your partner or spouse after your death. It's important to get quotes from the whole market as different providers will offer the best rates at different times and make sure you confirm health and lifestyle details as these can boost your income.
                            </p>
                            <p class="card-text">
                                Annuities can't be changed once set up so you need to choose what's right for you and get the best deal you can.
                            </p>
                            <p class="card-text">
                                <strong>Drawdown:</strong>
                            </p>
                            <p class="card-text">
                                "With drawdown you keep your pension invested and draw an income whenever you need to. If your investments perform well and you don't take too much out you could enjoy a rising income. But the danger is you could run out of money if your investments perform poorly, you take out too much too early, or you live longer than expected. This means it's a higher risk option than an annuity so it won't be right for everyone."
                            </p>
                            <p class="card-text">
                                <strong>One-off lump sums:</strong>
                            </p>
                            <p class="card-text">
                                "With annuities and drawdown you take the tax-free cash at the start, with lump sums or uncrystallised funds pension lump sums to give them their full name, UFPLS for short, it's different. 25% of each lump sum is usually tax-free, with the rest taxable, while the remainder of your pension pot stays invested. This means the risks are more like a drawdown plan and it's a higher risk option than an annuity.
                            </p>
                            <p class="card-text">
                                Important information - What you do with your pension is an important decision. You should check you're making the right decision for your circumstances and that you understand all your options and their risks. Tax rules can change over time and benefits depend on individal circumstances."
                            </p>
                            <p class="card-text">
                                The government's free and impartial Pension Wise service can help you and we can offer you advice if you'd like it. The information in this video isn't personal advice.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="row">
        <div class="col-md-9 mx-auto">
            <h4>
                Compare your retirement options
            </h4>
            <p>
                There are three main ways you can access your pension: by getting an annuity, moving money into drawdown and taking lump sums. You can also mix and match your retirement options, which could help you find the right balance of security and flexibility.
            </p>

            <h4>
                Annuities
            </h4>
            <p>
                Swap money in your pension for a guaranteed income for the rest of your life.
            </p>

            <table class="table table-bordered mt-4 mb-5">
                <thead>
                    <tr>
                        <th scope="col">Tax</th>
                        <th scope="col">Benefits</th>
                        <th scope="col">Risks</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>You can usually take up to 25% tax-free cash at the start. Your income is taxable.</td>
                        <td>
                            <bullet-point-list :items="annuityBenefits" :icon-style="BulletListIconStyle.Tick"></bullet-point-list>
                        </td>
                        <td>
                            <bullet-point-list :items="annuityRisks" :icon-style="BulletListIconStyle.Cross"></bullet-point-list>
                        </td>
                    </tr>
                </tbody>
            </table>

            <h4>
                Drawdown
            </h4>
            <p>
                Keep your pension invested. Take the income you want, when you want.
            </p>

            <table class="table table-bordered mb-5">
                <thead>
                    <tr>
                        <th scope="col">Tax</th>
                        <th scope="col">Benefits</th>
                        <th scope="col">Risks</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>You can usually take up to 25% tax-free cash at the start. Any income you withdraw is taxable.</td>
                        <td>
                            <bullet-point-list :items="drawdownBenefits" :icon-style="BulletListIconStyle.Tick"></bullet-point-list>
                        </td>
                        <td>
                            <bullet-point-list :items="drawdownRisks" :icon-style="BulletListIconStyle.Cross"></bullet-point-list>
                        </td>
                    </tr>
                </tbody>
            </table>

            <h4>
                Lump sums
            </h4>
            <p>
                Keep your pension invested. Take the lump sums you want, when you want.
            </p>

            <table class="table table-bordered mb-5">
                <thead>
                    <tr>
                        <th scope="col">Tax</th>
                        <th scope="col">Benefits</th>
                        <th scope="col">Risks</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Usually, 25% of each withdrawal is tax-free, and the rest is taxable.</td>
                        <td>
                            <bullet-point-list :items="lumpSumBenefits" :icon-style="BulletListIconStyle.Tick"></bullet-point-list>
                        </td>
                        <td>
                            <bullet-point-list :items="lumpSumRisks" :icon-style="BulletListIconStyle.Cross"></bullet-point-list>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup lang="ts">
import HeaderBanner from './HeaderBanner.vue';
import BulletPointList from '../common/BulletPointList.vue';
import { BulletListItem, BulletListIconStyle } from '@/guiide/ts/components/bulletListIconStyle';

const annuityBenefits = new Array<BulletListItem>(
    { text: "Your income is guaranteed for life. This is true no matter how long you live or what the stock markets are doing." },
    { text: "You can choose options so your income increases. This means your buying power could keep up with inflation." },
    { text: "Your income could continue after you die if you've chosen certain options when you get quotes and apply." }
);
const annuityRisks = new Array<BulletListItem>(
    { text: "You can't change your options if your circumstances change." },
    { text: "You can't cash in your annuity." },
    { text: "Annuity rates might rise in the future, but you won't benefit from this if your annuity is already being paid." },
);

const drawdownBenefits = new Array<BulletListItem>(
    { text: "Withdraw what you want, when you want. So you keep your options open if your circumstances change." },
    { text: "Potentially beat inflation with returns from your investments. You could maintain your buying power as prices rise." },
    { text: "Pass on your money - when you die this can normally be paid as a lump sum or as income." },
);
const drawdownRisks = new Array<BulletListItem>(
    { text: "You could run out of money if you withdraw too much too soon, your investments don't perform as you'd hoped or you live longer than expected." },
    { text: "Income isn't secure, it could fall or even stop completely." },
    { text: "It's possible you'll get back less than you originally invested, as all investments can fall as well as rise in value." }
);

const lumpSumBenefits = new Array<BulletListItem>(
    { text: "Withdraw what you want, when you want. So you keep your options open if your circumstances change." },
    { text: "Potentially beat inflation with returns from your investments. You could maintain your buying power as prices rise." },
    { text: "Pass on your money - when you die this can normally be paid as a lump sum or as income." }
);
const lumpSumRisks = new Array<BulletListItem>(
    { text: "You could run out of money if you withdraw too much too soon, your investments don't perform as you'd hoped or you live longer than expected." },
    { text: "Income isn't secure, it could fall or even stop completely." },
    { text: "It's possible you'll get back less than you originally invested, as all investments can fall as well as rise in value." },
);
</script>

<style scoped>
    .table th {
        --bs-table-accent-bg: #01ffc8 !important;
    }
</style>